import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import React, { useState } from "react";
import { Button, CheckPicker, SelectPicker } from "rsuite";
import { useTranslation } from "react-i18next";
import { encryptUrl } from "../../../navigation/useDecryptParams";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { useNavigate } from "react-router-dom";
import MusicScheduleSongsModal from "./MusicScheduleSongsModal";
import {
  weekAllDaysObjArr,
  weekDaysObjArr,
} from "../../../core/config/AppConstants";
import { getTodayShortDay, isEmptyVal } from "../../../utils/helpers";

const AddSongsToMusicScheduleBulkAction = ({
  musicScheduleData,
  checkedKeys,
  onAddSongs,
  onDeleteSongs,
  onSearchSongs,
  selectedSlot,
  setSelectedSlot,
  selectedDay,
  setSelectedDay,
  ...props
}: {
  musicScheduleData: any;
  checkedKeys: any;
  onAddSongs?: Function;
  onDeleteSongs?: Function;
  onSearchSongs?: Function;
  selectedSlot?: String;
  setSelectedSlot?: Function;
  selectedDay?: String;
  setSelectedDay?: Function;
}) => {
  const { t } = useTranslation();
  const [openBulkActionNoSelectionModal, setOpenBulkActionNoSelectionModal] =
    useState(false);
  const [alertDisplayText, setAlertDisplayText] = useState<any>(
    t("PLEASE_SELECT")
  );
  const [openModalByName, setOpenModalByName] = useState("");
  const ecryptedMusicScheduleId = encryptUrl(`${musicScheduleData.id}`);
  const [selecteDisplaydDay, setSelecteDisplaydDay] = useState<string[]>(
    selectedDay.split(",")
  );
  const navigate = useNavigate();

  function backToMusicSchedules() {
    navigate(ContentRoutes.MUSIC_SCHEDULES);
  }

  function bulkAdd() {
    if (
      musicScheduleData?.new_schedule_everyday.toString() === "1" &&
      isEmptyVal(selectedDay)
    ) {
      setAlertDisplayText(t("PLEASE_SELECT_FIELD", { field: t("DAY") }));
      setOpenBulkActionNoSelectionModal(true);
      return false;
    }
    if (isEmptyVal(selectedSlot)) {
      setAlertDisplayText(
        t("PLEASE_SELECT_FIELD", { field: t("DAY") + " " + t("PART") })
      );
      setOpenBulkActionNoSelectionModal(true);
      return false;
    }
    if (checkedKeys.length === 0) {
      setAlertDisplayText(t("PLEASE_SELECT_RECORD_TO_ACTION"));
      setOpenBulkActionNoSelectionModal(true);
      return false;
    }
    onAddSongs && onAddSongs();
  }

  function bulkDelete() {
    setOpenModalByName("");
    onDeleteSongs && onDeleteSongs();
  }

  const allDayValues = weekDaysObjArr
    .map((day) => day.value)
    .filter((v) => v !== "all");

  const handleOnDayChange = (selectedValues: string[]) => {
    if (selectedValues.includes("all")) {
      // Toggle: If "All Days" is selected, select or deselect all days
      if (selectedValues.length === 8) {
        // All days are selected, so clear the selection
        setSelecteDisplaydDay([]);
        setSelectedDay("");
        onSearchSongs?.({ exclude_music_day: "" });
      } else {
        // Select all days
        setSelecteDisplaydDay(allDayValues);
        setSelectedDay(allDayValues.join(",").replace(/^[, ]+|[, ]+$/g, ""));
        onSearchSongs?.({
          exclude_music_day: allDayValues
            .join(",")
            .replace(/^[, ]+|[, ]+$/g, ""),
        });
      }
    } else {
      // Update selected days normally (without "All Days")
      setSelecteDisplaydDay(selectedValues);
      setSelectedDay(selectedValues.join(",").replace(/^[, ]+|[, ]+$/g, ""));
      onSearchSongs?.({
        exclude_music_day: selectedValues
          .join(",")
          .replace(/^[, ]+|[, ]+$/g, ""),
      });
    }
  };

  return (
    <div className="add-music-schedule-toolbar">
      {UtilityServices.hasPermission("delete-song") && (
        <>
          <Button
            appearance="primary"
            className="add-music-schedule-toolbar-btn add-music-schedule-add-btn"
            onClick={bulkAdd}
          >
            {t("ADD")} {t("SONGS")} {t("TO")}
          </Button>

          {musicScheduleData?.new_schedule_everyday &&
            musicScheduleData?.new_schedule_everyday.toString() === "1" && (
              <div className="add-music-schedule-picker-wrap">
                <CheckPicker
                  sticky
                  className="add-music-schedule-picker"
                  onChange={handleOnDayChange}
                  onClean={() => {
                    setSelectedDay("");
                    onSearchSongs && onSearchSongs({ exclude_music_day: "" });
                  }}
                  data={weekAllDaysObjArr}
                  value={selecteDisplaydDay}
                  name="play_for_day"
                  placeholder={t("SELECT") + " " + t("DAY")}
                />
              </div>
            )}
          <div className="add-music-schedule-picker-wrap">
            <SelectPicker
              className="add-music-schedule-picker"
              data={musicScheduleData?.time_slots}
              valueKey="id"
              labelKey="slot_title"
              onSelect={(value, item, e) => {
                setSelectedSlot(value);
                onSearchSongs &&
                  onSearchSongs({ exclude_music_time_slot: value });
              }}
              onClean={(e: any) => {
                setSelectedSlot("");
                onSearchSongs && onSearchSongs({ exclude_music_time_slot: "" });
              }}
              name="time_slot"
              placeholder={t("SELECT") + " " + t("DAY") + " " + t("PART")}
            />
          </div>
          <Button
            appearance="subtle"
            className="add-music-schedule-toolbar-btn"
            onClick={backToMusicSchedules}
          >
            {t("BACK")} {t("TO")} {t("MUSIC_SCHEDULES")}
          </Button>
          <Button
            appearance="ghost"
            className="add-music-schedule-toolbar-btn"
            onClick={(e: any) =>
              setOpenModalByName("show-music-schedule-songs")
            }
          >
            {t("VIEW")} {t("MUSIC_SCHEDULE")} {t("SONGS")}
          </Button>
        </>
      )}
      <AlertModal
        open={openBulkActionNoSelectionModal}
        handleClose={(e: any) => {
          if (e) {
            setOpenBulkActionNoSelectionModal(false);
          }
        }}
        displayText={alertDisplayText}
        modalTitle={t("PLEASE_SELECT")}
        cancelButton={false}
      />
      {openModalByName === "show-music-schedule-songs" && (
        <MusicScheduleSongsModal
          musicScheduleId={musicScheduleData?.id}
          musicScheduleData={musicScheduleData}
          openModal={
            openModalByName === "show-music-schedule-songs" ? true : false
          }
          setOpenModal={setOpenModalByName}
          onDeleteSongs={bulkDelete}
          showPlayer={false}
        />
      )}
    </div>
  );
};

export default AddSongsToMusicScheduleBulkAction;
